import React from 'react';
import { FormattedMessage } from 'react-intl';

import { PARTS } from 'constants/ModeConstants';

import { createDictionary } from 'utils/ObjectUtils';

import { FOREIGN_ENTITY_TYPE } from './InterventionConstants';

export const DASHBOARD_SOURCE = 'chat-dashboard';

export const CONVERSATION_TYPES = {
  EXTERNAL: 'EXTERNAL',
  INTERNAL: 'INTERNAL'
};

export const VISIBILITY = { PUBLIC: 'PUBLIC', PRIVATE: 'PRIVATE' };

export const MAX_UNREAD_COUNT = 20;

const ChannelEnum = {
  INTERNAL: 'INTERNAL',
  SMS: 'SMS',
  EMAIL: 'EMAIL',
  WHATSAPP: 'WHATSAPP',
  DRIVER_APP: 'DRIVER_APP'
} as const;
export const CHANNEL = ChannelEnum;
export type ChannelEnum = typeof ChannelEnum[keyof typeof ChannelEnum];

export const CustomerType = {
  CUSTOMER: 'CUSTOMER',
  ADDITIONAL_CUSTOMER: 'ADDITIONAL_CUSTOMER'
} as const;
export type CustomerType = typeof CustomerType[keyof typeof CustomerType];

/**
 * @type {Record<
 *   import('modules/global/typing/enums/message.status.enum').MessageStatusEnum,
 *   import('modules/global/typing/enums/message.status.enum').MessageStatusEnum
 * >}
 */
export const STATUS = {
  CREATED: 'CREATED',
  NOT_SENT: 'NOT_SENT',
  PENDING: 'PENDING',
  READ: 'READ',
  SEEN: 'SEEN',
  SENDING: 'SENDING',
  SENT: 'SENT',
  UNREAD: 'UNREAD',
  UNSENT: 'UNSENT'
};

export const EVENT_TYPE = {
  APPOINTMENT_EVENT: 'APPOINTMENT_EVENT',
  CAMPAIGN_FILE_EVENT: 'CAMPAIGN_FILE_EVENT',
  INTERVENTION_EVENT: 'INTERVENTION_EVENT'
};

export const MESSAGE_TYPE = {
  MESSAGE: 'MESSAGE',
  ...EVENT_TYPE
} as const;

export type MessageTypeEnum = (typeof MESSAGE_TYPE)[keyof typeof MESSAGE_TYPE];

export const UI_TABS = {
  ALL_CLIENT: 'ALL_CLIENT',
  MY_CLIENT: 'MY_CLIENT',
  INTERNAL: 'INTERNAL'
};

export const ENTITY_PARAMS_BY_MODE = createDictionary({
  [PARTS]: 'partsInterventionId',
  CAMPAIGN: 'campaignFileId'
}, 'interventionId');

export const CONVERSATION_ENTITY_TYPE = {
  APPOINTMENT: 'APPOINTMENT',
  CAMPAIGN_FILE: 'CAMPAIGN_FILE',
  CONVERSATION: 'CONVERSATION',
  INTERVENTION: 'INTERVENTION',
  PARTS_INTERVENTION: 'PARTS_INTERVENTION'
};

export const MSG_ENTITY_TYPE_BY_FOREIGN_ENTITY_TYPE = createDictionary({
  [FOREIGN_ENTITY_TYPE.PARTS_INTERVENTION]: CONVERSATION_ENTITY_TYPE.PARTS_INTERVENTION,
  [FOREIGN_ENTITY_TYPE.INTERVENTION]: CONVERSATION_ENTITY_TYPE.INTERVENTION,
  [FOREIGN_ENTITY_TYPE.APPOINTMENT]: CONVERSATION_ENTITY_TYPE.APPOINTMENT,
  [FOREIGN_ENTITY_TYPE.CAMPAIGN_FILE]: CONVERSATION_ENTITY_TYPE.CAMPAIGN_FILE
}, CONVERSATION_ENTITY_TYPE.CONVERSATION);

// Inverse of MSG_ENTITY_TYPE_BY_FOREIGN_ENTITY_TYPE: maps an entity type to the foreign id field
// expected by the message fetch filter (GET /messages/api/filter).
export const FOREIGN_ID_FIELD_BY_ENTITY_TYPE = {
  [CONVERSATION_ENTITY_TYPE.INTERVENTION]: 'interventionId',
  [CONVERSATION_ENTITY_TYPE.PARTS_INTERVENTION]: 'partsInterventionId',
  [CONVERSATION_ENTITY_TYPE.APPOINTMENT]: 'appointmentId',
  [CONVERSATION_ENTITY_TYPE.CAMPAIGN_FILE]: 'campaignFileId',
  [CONVERSATION_ENTITY_TYPE.CONVERSATION]: 'conversationId'
};

export const MSG_TAG_REGEX = /{{(.*?)}}/g;

export const SENDABLE_CHANNELS = [CHANNEL.SMS, CHANNEL.EMAIL, CHANNEL.WHATSAPP];

export const WHATSAPP_STATUS = {
  REACHABLE: 'REACHABLE',
  UNREACHABLE: 'UNREACHABLE'
} as const;

export type WhatsAppEnum = typeof WHATSAPP_STATUS[keyof typeof WHATSAPP_STATUS];

const ICON_BY_STATUS = createDictionary(
  {
    [STATUS.NOT_SENT]: 'fb-icon--close',
    [STATUS.SENT]: 'fb-icon--check'
  },
  'fb-icon--clock'
);

export const ICON_BY_CHANNEL_STATUS = createDictionary(
  {
    [CHANNEL.WHATSAPP]: createDictionary(
      {
        [STATUS.SENDING]: 'fb-icon--check',
        [STATUS.NOT_SENT]: 'fb-icon--close',
        [STATUS.UNSENT]: 'fb-icon--close',
        [STATUS.SENT]: 'fb-icon--read',
        [STATUS.SEEN]: 'fb-icon--read'
      },
      'fb-icon--clock'
    )
  },
  ICON_BY_STATUS
);

export const STATUS_ICON_LABEL = createDictionary({
  [STATUS.UNSENT]: <FormattedMessage id="page.chat.details.not_delivered" defaultMessage="Not delivered" />,
  [STATUS.NOT_SENT]: <FormattedMessage id="page.chat.details.not_delivered" defaultMessage="Not delivered" />
}, null);
